import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime, withDrPrefix } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, FileText, Stethoscope } from "lucide-react";
import VitalsTrendChart from "./VitalsTrendChart";

function VitalsSummary({ v }) {
  if (!v) return null;
  const parts = [
    v.bp_systolic && `BP: ${v.bp_systolic}/${v.bp_diastolic}`,
    v.pulse_rate && `Pulse: ${v.pulse_rate}`,
    v.weight && `Wt: ${v.weight}kg`,
    v.bmi && `BMI: ${v.bmi}`,
    v.temperature && `Temp: ${v.temperature}°C`,
    v.spo2 && `SpO2: ${v.spo2}%`,
  ].filter(Boolean);
  if (!parts.length) return null;
  return <p className="text-[11px] text-muted-foreground">{parts.join("  ·  ")}</p>;
}

function ConsultationCard({ c }) {
  const [expanded, setExpanded] = useState(false);
  const diagnosesSummary = (c.diagnoses || []).map((d) => d.condition).join(", ");
  const rxCount = (c.prescription_items || []).length;

  const viewPdf = () => {
    window.open(`${api.defaults.baseURL}/consultations/${c.id}/prescription-pdf`, "_blank");
  };

  return (
    <div className="rounded-lg border border-navy/10 bg-white">
      <button onClick={() => setExpanded(!expanded)} className="flex w-full items-start justify-between p-4 text-left hover:bg-nude/20">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-3.5 w-3.5 text-navy" />
            <p className="text-sm font-semibold text-navy">{formatDateTime(c.created_at)}</p>
            <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${c.status === "completed" ? "bg-green-100 text-green-800" : "bg-blue-100 text-blue-800"}`}>
              {c.status || "draft"}
            </span>
          </div>
          {c.doctor_name && <p className="mt-0.5 text-xs text-muted-foreground">{withDrPrefix(c.doctor_name)}</p>}
          {/* Which package this visit was against, when it was one. Absent for a
              standalone consultation, which is a normal case. */}
          {c.treatment_name && (
            <p className="mt-0.5 text-xs text-navy">
              For: <span className="font-medium">{c.treatment_name}</span>
            </p>
          )}
          {diagnosesSummary && <p className="mt-1 text-xs font-medium text-ink">{diagnosesSummary}</p>}
          {rxCount > 0 && <p className="text-[11px] text-muted-foreground">{rxCount} medicine{rxCount > 1 ? "s" : ""} prescribed</p>}
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="border-t border-navy/5 px-4 py-3 space-y-3">
          {/* Vitals */}
          <VitalsSummary v={c.vitals} />

          {/* Chief Complaint */}
          {c.chief_complaint && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Chief Complaint</p>
              <p className="text-xs text-ink">{c.chief_complaint}</p>
            </div>
          )}

          {/* Diagnoses */}
          {c.diagnoses?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Diagnosis</p>
              {c.diagnoses.map((d, i) => (
                <p key={i} className="text-xs text-ink">
                  {d.is_primary ? "● " : "○ "}{d.condition}{d.details ? ` — ${d.details}` : ""}
                </p>
              ))}
            </div>
          )}

          {/* Prescription */}
          {c.prescription_items?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Prescription</p>
              <div className="mt-1 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left text-[10px] text-muted-foreground">
                      <th className="pb-1 pr-3">Drug</th>
                      <th className="pb-1 pr-3">Dosage</th>
                      <th className="pb-1 pr-3">Freq</th>
                      <th className="pb-1 pr-3">Duration</th>
                      <th className="pb-1">Instructions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.prescription_items.map((p, i) => (
                      <tr key={i} className="border-b border-navy/5 last:border-0">
                        <td className="py-1 pr-3 font-medium">{p.drug_name}</td>
                        <td className="py-1 pr-3">{p.dosage}</td>
                        <td className="py-1 pr-3">{p.frequency}</td>
                        <td className="py-1 pr-3">{p.duration}</td>
                        <td className="py-1 text-muted-foreground">{p.instructions}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Advice */}
          {c.advice && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Advice</p>
              <p className="text-xs text-ink">{c.advice}</p>
            </div>
          )}

          {/* Clinical Notes */}
          {c.clinical_notes && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Notes</p>
              <p className="text-xs text-ink">{c.clinical_notes}</p>
            </div>
          )}

          {/* Next visit */}
          {c.next_visit_date && (
            <p className="text-xs text-gold font-medium">Next visit: {formatDateTime(c.next_visit_date)}</p>
          )}

          {/* PDF button */}
          {c.status === "completed" && (
            <Button variant="outline" size="sm" onClick={viewPdf} className="border-navy/20 text-navy">
              <FileText className="mr-1.5 h-3.5 w-3.5" /> View Prescription
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// `excludeId` drops one consultation from the list — used by the consultation
// page so a doctor reviewing history doesn't see the visit they're mid-way
// through writing.
export default function ConsultationHistorySection({ patientId, excludeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patientId) return;
    api.get(`/patients/${patientId}/consultation-history`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [patientId]);

  if (loading) return <div className="py-6 text-center text-sm text-muted-foreground">Loading consultation history...</div>;
  const consultations = (data?.consultations || []).filter((c) => c.id !== excludeId);
  if (!consultations.length) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No previous consultations recorded.</p>;
  }

  return (
    <div className="space-y-6">
      {/* Vitals Trend */}
      {data.vitals_trend?.dates?.length >= 2 && (
        <div>
          <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Vitals Trend</h4>
          <VitalsTrendChart vitalsTrend={data.vitals_trend} />
        </div>
      )}

      {/* Timeline */}
      <div>
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Consultations ({consultations.length})
        </h4>
        <div className="space-y-2">
          {consultations.map((c) => <ConsultationCard key={c.id} c={c} />)}
        </div>
      </div>
    </div>
  );
}
